import React, { useState, useRef, useEffect } from "react";
import { useClickOutside } from "../../hooks/useClickOutside";

interface FilterState {
    status?: string;
}

interface YourRequestFilterProps {
    onClose: () => void;
    onFilterChange: (filters: FilterState) => void;
    activeFilters: FilterState; // Add this prop to receive the current filters
}

const YourRequestFilter: React.FC<YourRequestFilterProps> = ({ 
    onClose, 
    onFilterChange, 
    activeFilters 
}) => {
    // Initialize with the active filters
    const [filters, setFilters] = useState<FilterState>(activeFilters);
    const filterRef = useRef<HTMLDivElement | null>(null);

    // Update local state when activeFilters prop changes
    useEffect(() => {
        setFilters(activeFilters);
    }, [activeFilters]);

    useClickOutside(filterRef, onClose);

    const handleInputChange = (field: keyof FilterState, value: string) => {
        const newFilters = { ...filters, [field]: value };
        setFilters(newFilters);
    };

    const clearAllFilters = () => {
        const clearedFilters = { status: "" };
        setFilters(clearedFilters);
        onFilterChange(clearedFilters);
    };

    return (
        <div
            ref={filterRef}
            className="z-50 bg-white divide-y divide-gray-100 rounded-lg shadow-2xl border border-gray-200 w-[calc(100vw-2.5rem)] max-w-[360px] sm:max-w-none sm:w-80"
        >
            <div className="p-4 text-sm text-gray-700">
                {/* Status */}
                <div className="bg-indigo-200 bg-opacity-25 rounded-lg p-4 mb-4 border-b border-lightgray-100">
                    <p className="text-sm">Select Status</p>
                    <div className="flex sm:flex-nowrap flex-wrap items-center gap-4 mt-3">
                        <div className="w-full">
                            <select
                                value={filters.status || ""}
                                onChange={(e) => handleInputChange("status", e.target.value)}
                                className="block w-full border-none p-3 rounded-lg bg-white text-xs text-grease-100 placeholder-gray-400 focus:outline-none"
                            >
                                <option value="">All</option>
                                <option value="Pending">Pending</option>
                                <option value="Answered">Answered</option>
                                <option value="Update Requested">Update Requested</option>
                                <option value="Updated Answer">Updated Answer</option>
                                <option value="Expired">Expired</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                    <button onClick={onClose} className="flex gap-2 px-3 py-2 bg-blue-500 bg-opacity-25 rounded-md">
                        <p className="font-medium text-lightgray-200 text-xs">Cancel</p>
                    </button>
                    <div className="flex items-center gap-4">
                        <button onClick={clearAllFilters} className="flex gap-2 px-3 py-2 border border-[#FFBC00] rounded-md">
                            <p className="font-medium text-[#FFBC00] text-xs">Clear All</p>
                        </button>
                        <button onClick={() => onFilterChange(filters)} className="flex gap-2 px-3 py-2 bg-[#FFBC00] rounded-md">
                            <p className="font-medium text-[#F5F9FE] text-xs">Apply</p>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default YourRequestFilter;